import { useEffect, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Icon, Logo } from '@vault/ui';
import { api } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.js';
import { useTheme } from '../theme/ThemeContext.js';
import { useT } from '../i18n/index.js';
import type { MessageKey } from '../i18n/index.js';
import { CommandPalette } from './CommandPalette.js';
import { OfflineBanner, ConflictBanner } from '../pages/states/index.js';
import { UpgradeModal } from '../pages/gating/index.js';

const NAV: Array<{ to: string; labelKey: MessageKey; icon: Parameters<typeof Icon>[0]['name']; badge?: 'dupes' }> = [
  { to: '/timeline', labelKey: 'nav.timeline', icon: 'clock' },
  { to: '/search', labelKey: 'nav.search', icon: 'search' },
  { to: '/duplicates', labelKey: 'nav.duplicates', icon: 'grid', badge: 'dupes' },
  { to: '/collections', labelKey: 'nav.collections', icon: 'layers' },
  { to: '/import', labelKey: 'nav.import', icon: 'download' },
  { to: '/settings/trash', labelKey: 'nav.trash', icon: 'trash' },
  { to: '/settings', labelKey: 'nav.settings', icon: 'gear' },
];

export function AppShell() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const { t, plural } = useT();
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  // Another tab mutated the vault — offer a reload (see api client's tv-vault
  // BroadcastChannel; a channel never delivers to the tab that posted).
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel('tv-vault');
    ch.onmessage = (e) => {
      if (e.data === 'changed') setConflict(true);
    };
    return () => ch.close();
  }, []);

  const usage = useQuery({ queryKey: ['usage'], queryFn: () => api.account.usage() });
  const dupes = useQuery({ queryKey: ['dedup', 'summary'], queryFn: () => api.dedup.live(), staleTime: 60_000 });
  const dupCount = dupes.data?.groups.reduce((n, g) => n + Math.max(0, g.members.length - 1), 0) ?? 0;

  return (
    <div style={{ display: 'flex', height: '100vh', background: 'var(--bg)', color: 'var(--text-1)' }}>
      <OfflineBanner />
      <ConflictBanner open={conflict} onReload={() => window.location.reload()} onDismiss={() => setConflict(false)} />
      {/* ── sidebar ── */}
      <aside
        style={{
          width: 'var(--sidebar-w)',
          flexShrink: 0,
          background: 'var(--surface-0)',
          borderInlineEnd: '1px solid var(--line)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '16px 16px 14px' }}>
          <Logo size={26} />
          <span style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 700, letterSpacing: '-.02em' }}>
            Nomad Tabs
          </span>
        </div>

        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '4px 12px' }}>
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} style={({ isActive }) => navItemStyle(isActive)}>
              {({ isActive }) => (
                <>
                  <Icon name={n.icon} size={15} color={isActive ? 'var(--primary)' : 'currentColor'} />
                  {t(n.labelKey)}
                  {n.badge === 'dupes' && dupCount > 0 && (
                    <span style={badgeStyle}>{dupCount}</span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <span style={{ flex: 1 }} />

        {usage.data && (
          <div style={{ padding: '14px 16px', borderTop: '1px solid var(--line)', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'var(--font-mono)', fontSize: 9.5, color: 'var(--text-3)' }}>
              <span>{t('shell.saved')}</span>
              <span>{plural('unit.tabs', usage.data.counts.items)}</span>
            </span>
            {user?.plan === 'free' && (
              <button type="button" onClick={() => setUpgradeOpen(true)} style={upgradeStyle}>
                {t('common.upgradeToPro')}
              </button>
            )}
          </div>
        )}
      </aside>

      {/* ── main column ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {/* topbar */}
        <header
          style={{
            height: 'var(--topbar-h)',
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '0 20px',
            borderBottom: '1px solid var(--line)',
            background: 'var(--surface-0)',
          }}
        >
          <button onClick={() => setPaletteOpen(true)} style={cmdkStyle}>
            <Icon name="search" size={14} color="var(--text-3)" />
            <span style={{ color: 'var(--text-3)' }}>{t('shell.searchPlaceholder')}</span>
            <kbd style={kbdStyle}>⌘K</kbd>
          </button>
          <span style={{ flex: 1 }} />
          <button onClick={toggle} title={t('shell.toggleTheme')} style={iconBtnStyle}>
            <Icon name={theme === 'dark' ? 'sun' : 'moon'} size={16} />
          </button>
          <button onClick={() => void logout()} title={user?.email} style={avatarStyle}>
            {(user?.email ?? '?').charAt(0).toUpperCase()}
          </button>
        </header>

        <main style={{ flex: 1, overflow: 'auto' }}>
          <Outlet />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </div>
  );
}

function navItemStyle(isActive: boolean): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    height: 36,
    padding: '0 10px',
    borderRadius: 10,
    fontSize: 13,
    fontWeight: isActive ? 600 : 500,
    color: isActive ? 'var(--text-1)' : 'var(--text-2)',
    background: isActive ? 'var(--surface-2)' : 'transparent',
    border: isActive ? '1px solid var(--line)' : '1px solid transparent',
    cursor: 'pointer',
  };
}

const badgeStyle: React.CSSProperties = {
  marginInlineStart: 'auto',
  display: 'inline-flex',
  background: 'var(--amber-tint)',
  border: '1px solid var(--amber-line)',
  borderRadius: 999,
  padding: '1px 7px',
  fontFamily: 'var(--font-mono)',
  fontSize: 9.5,
  fontWeight: 600,
  color: 'var(--amber-ink)',
};

const upgradeStyle: React.CSSProperties = {
  height: 34,
  borderRadius: 999,
  border: '1px solid var(--line-primary)',
  background: 'var(--primary-tint)',
  color: 'var(--primary)',
  fontSize: 12.5,
  fontWeight: 600,
  cursor: 'pointer',
};

const cmdkStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  height: 34,
  padding: '0 12px',
  width: 320,
  borderRadius: 999,
  border: '1px solid var(--line)',
  background: 'var(--surface-1)',
  fontSize: 13,
  cursor: 'pointer',
};

const kbdStyle: React.CSSProperties = {
  marginInlineStart: 'auto',
  fontFamily: 'var(--font-mono)',
  fontSize: 10,
  color: 'var(--text-3)',
  border: '1px solid var(--line)',
  borderRadius: 6,
  padding: '1px 5px',
};

const iconBtnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 34,
  height: 34,
  borderRadius: 8,
  border: '1px solid var(--line)',
  background: 'transparent',
  color: 'var(--text-2)',
  cursor: 'pointer',
};

const avatarStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  borderRadius: '50%',
  background: 'var(--grad-mark)',
  color: '#fff',
  fontSize: 12,
  fontWeight: 600,
  border: 'none',
  cursor: 'pointer',
};
